export interface Vec2 {
  x: number;
  y: number;
}

export function vec2(x = 0, y = 0): Vec2 {
  return { x, y };
}

export function length(v: Vec2): number {
  return Math.hypot(v.x, v.y);
}

/**
 * Scale a vector so its length never exceeds `max`.
 * Shorter vectors pass through untouched, which is what keeps analogue sticks
 * and touch drags pressure-sensitive instead of snapping to full speed.
 */
export function clampLength(v: Vec2, max: number): Vec2 {
  const len = length(v);
  if (len <= max || len === 0) return { x: v.x, y: v.y };
  const scale = max / len;
  return { x: v.x * scale, y: v.y * scale };
}

/**
 * Radial deadzone with rescaling.
 *
 * Naively zeroing inputs below the threshold leaves a discontinuity: the stick
 * jumps from 0 to `deadzone` the instant it engages. Rescaling the remaining
 * range back to 0..1 keeps slow movement available.
 */
export function applyDeadzone(v: Vec2, deadzone: number): Vec2 {
  const len = length(v);
  if (len <= deadzone) return { x: 0, y: 0 };
  const scaled = Math.min(1, (len - deadzone) / (1 - deadzone)) / len;
  return { x: v.x * scaled, y: v.y * scaled };
}

export function approxEquals(a: Vec2, b: Vec2, epsilon = 1e-6): boolean {
  return Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon;
}
